import React, { useState } from 'react';
import { Box, Table, TableBody, TableHead, TableRow, TableCell, FormControl, makeStyles, Theme, createStyles, Grid } from '@material-ui/core';
import { useTypedTranslation } from '@app/hooks';
import { PasslyTableRow } from '@app/componentsV2';
import { PasslyTableCell } from '@app/components/Table';
import { PasslyButton } from '@app/components/Buttons';
import { KNOWN_ATTRIBUTE_TYPES, CLAIM_PROPERTIES, NAME_IDENTIFIER_ATTRIBUTE, availableAttributeDisplayName } from '@app/pages/ssoManager/addEditApplication.enums';
import { availableAttributes } from '@app/pages/ssoManager/addEditApplication.enums';
import { ExtendedPropertiesComponent } from '../components/extendedPropertiesComponent';
import { ICustomAttribute } from '@app/models';
import { Autocomplete } from '@material-ui/lab';
import { PasslyCheckbox, PasslyTextField } from '@app/components';


interface IProps {
    customAttributeMaps: ICustomAttribute[];
    onGet: (index: number) => any;
    onDelete: (index: number) => void;
    onAdd: (index: number, data: any) => void;
}

interface IExtendedProperties {
    newAttributeClaimFormat: string;
    newAttributeClaimDisplayName: string;
    newAttributeClaimAttributeName: string;
    newAttributeClaimAttributeNameQualifier: string;
    newAttributeClaimSPNameQualifier: string;
    newAttributeClaimSPProvidedId: string;
}

interface IState {
    attrMapVisible: boolean;
    newAttributeValue: string;
    newAttributeType: string;
    extendedPropertiesEnabled: boolean;
    extendedProperties: IExtendedProperties;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        'custom-mapping': {
            maxWidth: '100%',
            overflow: 'auto',
            marginBottom: theme.spacing(2),
            marginTop: theme.spacing(2),
        },
        buttons: {
            '& > *': {
                marginRight: theme.spacing(1),
            },
        },
        'attribute-arrow': {
            margin: '32px 0 0 35px',
        },
        'table-cell': {
            display: 'table-cell',
            padding: '0 16px',
        }
    }),
);

export const AttributeAsCustomComponent: React.SFC<IProps> = ({ customAttributeMaps, onGet, onDelete, onAdd }: IProps) => {

    const { translation } = useTypedTranslation();

    const classes = useStyles();

    const initialState = {
        attrMapVisible: false,
        extendedPropertiesEnabled: false,
        editIndex: -1,
    };

    const [state, setState] = useState(initialState);

    const newAttributeInitialState = {
        newAttributeValue: '',
        newAttributeType: '',
        extendedProperties: {
            newAttributeClaimFormat: '',
            newAttributeClaimDisplayName: '',
            newAttributeClaimAttributeName: '',
            newAttributeClaimAttributeNameQualifier: '',
            newAttributeClaimSPNameQualifier: '',
            newAttributeClaimSPProvidedId: ''
        }
    };

    const [newAttribute, setNewAttribute] = useState(newAttributeInitialState);

    const attrMapVisibleToggle = () => {
        setState({ ...state, attrMapVisible: !state.attrMapVisible });
    };

    const onEdit = (index: number) => {
        setState({ ...state, attrMapVisible: true, editIndex: index });
        const customAttribute = onGet(index);

        newAttribute.newAttributeValue = customAttribute.Emit;
        newAttribute.newAttributeType = customAttribute.Type;
        newAttribute.extendedProperties.newAttributeClaimFormat = customAttribute.Properties[CLAIM_PROPERTIES.Format];
        newAttribute.extendedProperties.newAttributeClaimDisplayName = customAttribute.Properties[CLAIM_PROPERTIES.DisplayName];
        newAttribute.extendedProperties.newAttributeClaimAttributeName = customAttribute.Properties[CLAIM_PROPERTIES.AttributeNameFormat];
        newAttribute.extendedProperties.newAttributeClaimAttributeNameQualifier = customAttribute.Properties[CLAIM_PROPERTIES.NameQualifier];
        newAttribute.extendedProperties.newAttributeClaimSPNameQualifier = customAttribute.Properties[CLAIM_PROPERTIES.SPNameQualifier];
        newAttribute.extendedProperties.newAttributeClaimSPProvidedId = customAttribute.Properties[CLAIM_PROPERTIES.SPProvidedID];
    };

    const renderTableRows = (item: ICustomAttribute, index: number) => {
        return (
            <PasslyTableRow key={index}>
                <PasslyTableCell>
                    {item.Emit}
                </PasslyTableCell>
                <PasslyTableCell>
                    <pre><code>{'=>'}</code></pre>
                </PasslyTableCell>
                <PasslyTableCell>
                    {item.Type}
                </PasslyTableCell>
                <PasslyTableCell>
                    <span className={classes['table-cell']}>
                        <PasslyButton variant='contained' color='primary' onClick={() => onEdit(index)}>{translation('EDIT')}</PasslyButton>
                    </span>
                    <span className={classes['table-cell']}>
                        <PasslyButton variant='contained' color='primary' onClick={() => onDelete(index)}>{translation('REMOVE')}</PasslyButton>
                    </span>
                </PasslyTableCell>
            </PasslyTableRow>
        );
    };

    const customAttrsPresent = () => {
        return customAttributeMaps?.length > 0;
    };

    const cancelAddAttrMap = () => {
        setState({ ...state, attrMapVisible: false, editIndex: -1 });
        setNewAttribute(newAttributeInitialState);
    };

    const addAttrMap = () => {
        let props = {};

        const setProp = (p: any, name: string, value: string) => {
            if (value) {
                p[name] = value;
            }
        };

        const { newAttributeClaimFormat, newAttributeClaimDisplayName, newAttributeClaimAttributeName, newAttributeClaimAttributeNameQualifier, newAttributeClaimSPNameQualifier, newAttributeClaimSPProvidedId } = newAttribute.extendedProperties;

        if (state.extendedPropertiesEnabled) {
            setProp(props, CLAIM_PROPERTIES.Format, newAttributeClaimFormat);
            setProp(props, CLAIM_PROPERTIES.DisplayName, newAttributeClaimDisplayName);
            setProp(props, CLAIM_PROPERTIES.AttributeNameFormat, newAttributeClaimAttributeName);
            setProp(props, CLAIM_PROPERTIES.NameQualifier, newAttributeClaimAttributeNameQualifier);
            setProp(props, CLAIM_PROPERTIES.SPNameQualifier, newAttributeClaimSPNameQualifier);
            setProp(props, CLAIM_PROPERTIES.SPProvidedID, newAttributeClaimSPProvidedId);
        }

        onAdd(state.editIndex, { Emit: newAttribute.newAttributeValue, Type: newAttribute.newAttributeType, Properties: props });
        cancelAddAttrMap();
    };

    const handleStateChange = (name: string, value: any) => {
        setState({ ...state, [name]: value });
    };

    const handleNewAttributeChange = (name: string, value: any) => {
        value = value === null ? '' : value;
        setNewAttribute({ ...newAttribute, [name]: value });
    };

    const handleExtendedPropertiesChange = (name: string, value: string) => {
        value = value === null ? '' : value;
        setNewAttribute({ ...newAttribute, extendedProperties: { ...newAttribute.extendedProperties, [name]: value } });
    };

    const handleCheckboxChange = (checked: boolean, event: any) => {
        handleStateChange(event.target.name, checked);
    };

    return (
        <>
<div className={classes['custom-mapping']}>
    {customAttrsPresent() ? (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translation('ATTR_MAP_FROM')}</TableCell>
                                <TableCell/>
                                <TableCell>{translation('ATTR_MAP_TO')}</TableCell>
                                <TableCell/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {customAttributeMaps.map((item, index) => (
                                renderTableRows(item, index)
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                        // CUSTOM ATTRIBUTES ARE EMPTY
                        <div className='empty-map-warning warning'>
                            <div className='label warning-text'><b>{translation('CUSTOM_ATTR_MAP_EMPTY')}</b></div>
                            <code>{`${availableAttributeDisplayName} => ${NAME_IDENTIFIER_ATTRIBUTE}`}</code>
                        </div>
                    )}
</div>

{/* BUTTONS */}
{!state.attrMapVisible && (
                <Box>
                    <PasslyButton variant='contained' color='primary' onClick={attrMapVisibleToggle}>{translation('SHOW_ATTR_MAP_ADD')}</PasslyButton>
                </Box>
            )}
{state.attrMapVisible && (
                <>
                    <Grid container direction='row' justifyContent='center'>
                        {/* ADDING ATTRIBUTE */}
                        <Grid item md={4}>
                            <Autocomplete
                                id='newAttribute'
                                freeSolo
                                fullWidth
                                autoSelect
                                options={availableAttributes.map((attribute) => attribute)}
                                value={newAttribute.newAttributeValue}
                                onChange={(event: any, inputValue: string | null) => handleNewAttributeChange('newAttributeValue', inputValue)}
                                renderInput={(params) => (
                                    <PasslyTextField {...params} fullWidth label={translation('ATTRIBUTE_SELECTOR_NAME')} helperText={translation('ATTR_SELECTOR_EXTENDED_HELP')} />
                                )}
                            />
                        </Grid>
                        <Grid item md={1}>
                            <FormControl className={classes['attribute-arrow']}>
                                <code>{'=>'}</code>
                            </FormControl>
                        </Grid>
                        <Grid item md={7}>
                            <Autocomplete
                                id='newAttributeType'
                                freeSolo
                                fullWidth
                                autoSelect
                                options={KNOWN_ATTRIBUTE_TYPES.map((attributeType) => attributeType)}
                                value={newAttribute.newAttributeType}
                                onChange={(event: any, newValue: string | null) => handleNewAttributeChange('newAttributeType', newValue)}
                                renderInput={(params) => (
                                    <PasslyTextField {...params} fullWidth label={translation('ATTRIBUTE_SELECTOR_TYPE')} helperText={translation('ATTR_SELECTOR_TYPE_EXTENDED_HELP')} />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Box>
                        {/* EXTENDED PROPERTIES */}
                        <PasslyCheckbox
                            name='extendedPropertiesEnabled'
                            checked={state.extendedPropertiesEnabled}
                            onChange={handleCheckboxChange}
                            label={translation('ENABLED_EXTENDED_PROPERTIES')}
                        />
                        {state.extendedPropertiesEnabled && (
                            <ExtendedPropertiesComponent {...newAttribute.extendedProperties} onChange={handleExtendedPropertiesChange} />
                        )}
                    </Box>
                    <Box mt={4} className={classes.buttons} display='flex' justifyContent='flex-start'>
                        <PasslyButton variant='contained' color='primary' disabled={newAttribute.newAttributeValue === '' || newAttribute.newAttributeType === ''} onClick={addAttrMap}>
                            {state.editIndex >= 0
                                ?
                                translation('UPDATE_ATTR_MAPPING')
                                :
                                translation('SAVE_ATTR_MAPPING')}
                            </PasslyButton>
                        <PasslyButton variant='outlined' onClick={cancelAddAttrMap}>{translation('CANCEL')}</PasslyButton>
                    </Box>
                </>
            )}
</>
    );
};
